/**
 * The fourteen capabilities of the original aftermarket wheel, and what each
 * one is in the OEM+ workspace ("Lineage" strip on every module page).
 *
 * Source: `const AREAS=[ … ]` at line 2684 of
 * `/Users/timwark/Projects/Data/OEM+/OEM+_Platform_Demo.html` (14 records).
 * Consumer: `renderModule()` at line ~3028, which renders `then` under a
 * **Was:** label, `now` as the body, `agent` and `stat()` on the meta line, and
 * `links` as a row of source buttons.
 *
 * Porting decisions
 * - `links` were `[label, handlerString]` tuples. Each is now an `AreaLink`:
 *     `"show('fleet')"`                      → `action: act('fleet')`
 *     `"show('partsflow','bom')"`            → `action: act('partsflow','bom')`
 *     `"show('data');openDoc('x:…',true)"`   → `action: act('data')` plus
 *                                              `openDoc: 'x:…'` (data, not a handler)
 *     `"audit('fleet',{…})"` / `"ask('…')"`  → handler DROPPED, `action` omitted,
 *                                              with a `TODO(P3)` comment on the
 *                                              line naming what it did.
 *   Seven links are `audit(...)` drawer openers and one is an `ask(...)`
 *   assistant prompt; all eight are marked TODO(P3) below.
 * - `stat` was a zero-argument arrow returning a template literal over live
 *   demo globals (`FLEET`, `HEALTH`, `WOS`, `B.*`, `CONN`, `ALERTS`,
 *   `INCIDENTS`, `notifications()`). A seed file cannot evaluate those, so the
 *   template is preserved losslessly as `stat: readonly AreaStatPart[]`:
 *   literal text is a `'text'` part (verbatim, spacing included) and each
 *   `${…}` is an `'expr'` part holding the original JavaScript expression as an
 *   inert string. A later lane computes the exprs against the real store; until
 *   then a scene can render the text parts alone.
 *   Two exprs were inlined from locals the demo declared inside the arrow body:
 *   the `rating` area's `v` (`B.css.rows.map(r=>r[10])`) is substituted into
 *   both of its expressions.
 * - `NEW:1` became the optional boolean `isNew`. No demo consumer reads it
 *   (`cov` drives the styling), but it marks the nine capabilities the original
 *   wheel did not have, so it is kept.
 * - `cov` is the closed set `'full' | 'new'`; `'full'` renders the pill
 *   "Asset & work", `'new'` renders "Service operations".
 * - No markup was stripped: these strings are plain text.
 *
 * Data Schema layer — pure data, no behaviour.
 */

import { act, type OemAction } from './action';

/** Which half of the model a capability sits in. */
export type AreaCoverage = 'full' | 'new';

/** A deep link off a capability card. */
export interface AreaLink {
  /** Button text, verbatim. */
  readonly label: string;
  /** Where it navigates. Absent when the original handler was not a `show()` call. */
  readonly action?: OemAction;
  /** A file the original handler also opened via `openDoc(key, true)`. */
  readonly openDoc?: string;
}

/** One piece of the "Live:" stat line: literal text, or an expression to evaluate. */
export type AreaStatPart =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'expr'; readonly expr: string };

/** One capability of the aftermarket wheel. */
export interface Area {
  /** Capability key; modules reference these in their `caps` list. */
  readonly id: string;
  /** Number on the wheel, 1–14; rendered zero-padded. */
  readonly n: number;
  /** Capability name. */
  readonly name: string;
  /** True for the capabilities the original programme did not have. */
  readonly isNew?: boolean;
  /** What the original wheel promised — rendered after a **Was:** label. */
  readonly then: string;
  /** What it is in the workspace now. */
  readonly now: string;
  readonly cov: AreaCoverage;
  /** Agent (or agents) that run it. */
  readonly agent: string;
  readonly links: readonly AreaLink[];
  /** The "Live:" line, as text/expression parts. */
  readonly stat: readonly AreaStatPart[];
}

const t = (text: string): AreaStatPart => ({ kind: 'text', text });
const x = (expr: string): AreaStatPart => ({ kind: 'expr', expr });

/** Pill copy per coverage band, as the demo renders it. */
export const AREA_COVERAGE_LABEL: Readonly<Record<AreaCoverage, string>> = {
  full: 'Asset & work',
  new: 'Service operations',
};

/** The fourteen capabilities, in wheel order. */
export const AREAS: readonly Area[] = [
  {
    id: 'equipment',
    n: 1,
    name: 'Intelligent equipment',
    then: 'Wireless comms · vital signs · hour-based warnings · failure modes · history',
    now: 'Every machine streams or exports its vital signs (live MQTT, the RMS feed on the surface drills, shift-end Pitram elsewhere). The Diagnostic agent turns them into a condition score per component, a ranked failure mode and a time-to-failure window — and raises the alert itself, inside the shift where the data is live.',
    cov: 'full',
    agent: 'Diagnostic',
    links: [
      { label: 'Fleet health', action: act('fleet') },
      { label: 'Issues now', action: act('issues') },
      { label: 'S-09 live feed', action: act('machine', 'S-09') },
    ],
    stat: [
      x('FLEET.length'),
      t(' machines · '),
      x('ALERTS.length'),
      t(' open alerts · '),
      x('HEALTH.filter(h=>ATTN.includes(h.band)).length'),
      t(' flagged components'),
    ],
  },
  {
    id: 'population',
    isNew: true,
    n: 2,
    name: 'Machine & drifter population list',
    then: 'Per serial number · activity · location · bridging units · hot spots',
    now: 'One register of every serial number with its hours, location, who operates it, what it is bridging for and its hot spots — kept current from the fleet register, the RMS/live feeds and the condition register rather than a spreadsheet someone updates.',
    cov: 'new',
    agent: 'Fleet learning',
    links: [
      { label: 'Population list', action: act('population') },
      // TODO(P3): original handler was audit('fleet',{}) — open the source-rows drawer on the fleet register with no filter.
      { label: 'Fleet register rows' },
    ],
    stat: [
      x('FLEET.length'),
      t(' serials · '),
      x('B.rms_status.length'),
      t(' on RMS · '),
      x('HEALTH.filter(h=>h.health_score<=40).length'),
      t(' hot spots'),
    ],
  },
  {
    id: 'sos',
    isNew: true,
    n: 3,
    name: 'S.O.S daily report',
    then: 'Daily hotspots · activity · WIP · evaluation & prompt',
    now: 'The morning report writes itself at 06:00: hotspots from the condition register, activity from the technician app and the CMMS, work in progress from approvals and the parts pipeline, and one prompt per site lead. Ask the assistant for it, or open it here — per site or across all six.',
    cov: 'new',
    agent: 'Customer relations',
    links: [
      { label: 'S.O.S report', action: act('sos') },
      // TODO(P3): original handler was ask("Generate today's S.O.S report") — seed the assistant composer with that prompt.
      { label: 'Ask the assistant' },
    ],
    stat: [
      x("FLEET.filter(f=>['major','vpoor'].includes(STATUS(f.machine_id))).length"),
      t(' hotspots this morning · '),
      x("WOS.filter(w=>w.status.startsWith('Draft')).length"),
      t(' drafts in WIP'),
    ],
  },
  {
    id: 'jobcards',
    isNew: true,
    n: 4,
    name: 'Work instructions',
    then: 'Job card · possible causes · tools needed · quotes · steps to follow',
    now: "A job card is assembled when the job is: possible causes ranked from the fleet's own failure history, the tools for that system, a quote from the rate card and the catalogue, and the steps from the controlled procedure — pushed to the technician's headset through the Maximo Mobile app.",
    cov: 'new',
    agent: 'Knowledge + Technician copilot',
    links: [
      { label: 'Job cards', action: act('jobcards') },
      { label: 'Procedures', action: act('data'), openDoc: 'doc:SP-MS120-DTH-021' },
    ],
    stat: [
      x('B.tapp_jobs.length'),
      t(' jobs · '),
      x("B.docs.filter(d=>d.file.startsWith('SP-')).length"),
      t(' controlled procedures'),
    ],
  },
  {
    id: 'backtrack',
    n: 5,
    name: 'Back tracking',
    then: 'Automated job management & reporting',
    now: "Every job is tracked from detection to learning without anyone typing a report: the workflow phases, the technician-app labour lines and parts, the close-out in SAP PM or the customer's Maximo, and the cost attribution — all linked by the work-order number across systems.",
    cov: 'full',
    agent: 'Orchestrator + Work order',
    links: [
      { label: 'Job run', action: act('jobrun') },
      { label: 'Maximo cross-reference', action: act('data'), openDoc: 'x:MXWO_GDEEP' },
    ],
    stat: [
      x('WOS.length.toLocaleString()'),
      t(' work orders · '),
      x('B.maximo_assets.length'),
      t(' assets mirrored in Maximo'),
    ],
  },
  {
    id: 'hr',
    isNew: true,
    n: 6,
    name: 'HR allocation',
    then: 'Availability · skills / qualification · classification · posted · customer preference · GPS',
    now: 'The roster, the LMS and the technician app give the agent who is on swing, what they are certified for, where they are and who the customer prefers — so dispatch is a proposal with a reason, and an expiring certificate is flagged before it stops someone working.',
    cov: 'new',
    agent: 'Technician dispatch & rating',
    links: [
      { label: 'Technicians', action: act('techs') },
      // TODO(P3): original handler was audit('roster',{}) — open the source-rows drawer on the roster table.
      { label: 'Roster' },
      // TODO(P3): original handler was audit('lms',{}) — open the source-rows drawer on the LMS training records.
      { label: 'Training records' },
    ],
    stat: [
      x("B.tapp_techs.filter(t=>t.status==='On site').length"),
      t(' of '),
      x('B.tapp_techs.length'),
      t(' technicians on site · '),
      x("B.lms.filter(r=>r.status!=='Current'&&r.org==='Meridian Equipment').length"),
      t(' certificates to renew'),
    ],
  },
  {
    id: 'rating',
    isNew: true,
    n: 7,
    name: 'Real-time tech rating',
    then: 'Skills · performance · satisfied customers · complaints',
    now: "Rating is measured, not surveyed alone: fix-first-time from repeat work orders, job hours against the quote, the customer's CSS score per job and any complaint case — updated as each job closes, visible to the technician as well as the manager.",
    cov: 'new',
    agent: 'Technician dispatch & rating',
    links: [
      { label: 'Technician ratings', action: act('techs') },
      // TODO(P3): original handler was audit('css',{}) — open the source-rows drawer on the CSS responses.
      { label: 'CSS by technician' },
    ],
    stat: [
      t('CSS mean '),
      x('(B.css.rows.map(r=>r[10]).reduce((a,b)=>a+b,0)/B.css.rows.length).toFixed(1)'),
      t('/5 across '),
      x('B.css.rows.length'),
      t(' jobs'),
    ],
  },
  {
    id: 'css',
    isNew: true,
    n: 8,
    name: 'Real-time CSS',
    then: 'Daily rating · quality · responsiveness · reporting',
    now: 'A short in-app survey after every job feeds the same three scores the original programme asked for — quality, responsiveness, reporting — per customer, per site, per technician, with the comment routed to a case when it is a complaint.',
    cov: 'new',
    agent: 'Customer relations',
    links: [
      { label: 'Customers', action: act('customers') },
      // TODO(P3): original handler was audit('css',{}) — open the source-rows drawer on the CSS responses.
      { label: 'Responses' },
    ],
    stat: [
      x('B.css.rows.length'),
      t(' responses this month · '),
      x('B.css.rows.filter(r=>r[10]<3.5).length'),
      t(' below 3.5'),
    ],
  },
  {
    id: 'notify',
    isNew: true,
    n: 9,
    name: 'Customer info & notification',
    then: 'Customer information & notification system',
    now: "Each customer contact gets what changed, in their channel: an alert raised, a technician's ETA, a part landing, a bulletin to acknowledge, a report ready. The agent drafts every notification; nothing goes out that a person would not have sent.",
    cov: 'new',
    agent: 'Customer relations',
    links: [{ label: 'Customers & notifications', action: act('customers') }],
    stat: [x('notifications().length'), t(' notifications this fortnight · 7 customer organisations')],
  },
  {
    id: 'safety',
    n: 10,
    name: 'Safety & incident management',
    then: 'Safety & incident management system',
    now: 'Every agent action passes the safety check (isolation, bulletins, seismic protocol, site permits) and can be vetoed. Incidents are captured with the data that preceded them and replayed through the chain, so the lesson becomes a rule for the whole fleet rather than a paragraph in a report.',
    cov: 'full',
    agent: 'Safety & compliance',
    links: [
      { label: 'Incidents', action: act('incident') },
      { label: 'Bulletins', action: act('data'), openDoc: 'x:SB-2026-004' },
    ],
    stat: [
      x('INCIDENTS.length'),
      t(' incidents replayed · '),
      x('B.bulletins.length'),
      t(' bulletins issued'),
    ],
  },
  {
    id: 'sales',
    isNew: true,
    n: 11,
    name: 'Parts sales',
    then: 'Order intake · processing · link to stock · delivery',
    now: 'Order intake starts from the alert or the job, not a phone call: the agent resolves the part, checks stock at the site store and the distribution centre, prices it, picks road or air by the failure window and tracks it to delivery — the planner approves.',
    cov: 'new',
    agent: 'Parts sales',
    links: [
      { label: 'Parts pipeline', action: act('partsflow') },
      // TODO(P3): original handler was audit('orders',{}) — open the source-rows drawer on the orders table.
      { label: 'Orders' },
    ],
    stat: [
      x('B.orders.filter(o=>/agent/i.test(o.status)).length'),
      t(' agent requisitions · '),
      x('B.orders.filter(o=>/air/i.test(o.status)).length'),
      t(' on air freight'),
    ],
  },
  {
    id: 'allocation',
    isNew: true,
    n: 12,
    name: 'Parts allocation',
    then: 'Link to stock · BOM · delivery · pricing',
    now: 'The digital parts book gives the agent the bill of materials behind every drawing callout, so a technician can ask for "item 3 on section 07" and get the part number, its price, its stock and a delivery date in one answer.',
    cov: 'new',
    agent: 'Parts sales + Knowledge',
    links: [
      { label: 'BOM lookup', action: act('partsflow', 'bom') },
      { label: 'Parts books', action: act('data'), openDoc: 'x:MS-120_Parts-Book' },
    ],
    stat: [x('B.catbase_bom.length'), t(' BOM lines · '), x('B.parts.length'), t(' catalogue items')],
  },
  {
    id: 'inventory',
    isNew: true,
    n: 13,
    name: 'Inventory management',
    then: 'Minimum levels · predictive usage · inventory control',
    now: "Minimum levels come from predicted usage rather than last year's consumption: every flagged component maps to the parts it will need inside its failure window, per site, against what is actually on the shelf — and the agent proposes the minimum-level change.",
    cov: 'new',
    agent: 'Parts sales + Fleet learning',
    links: [
      { label: 'Predictive usage', action: act('partsflow', 'predict') },
      // TODO(P3): original handler was audit('inv',{qty_on_hand:(v,r)=>v<r.min_level}) — open the source-rows drawer on inventory, filtered to lines below minimum.
      { label: 'Inventory rows' },
    ],
    stat: [
      x("B.inventory.filter(i=>i.qty_on_hand<i.min_level&&i.location.includes('site')).length"),
      t(' site-store lines below minimum · '),
      x('B.inventory.length'),
      t(' stock lines'),
    ],
  },
  {
    id: 'cmms',
    n: 14,
    name: 'The CMMS principle',
    then: 'One system of record for assets and work (the "Maximo principle")',
    now: "Meridian's own Maximo is the aftermarket system of record — one asset record per machine with its meters, components, PM schedule, history, contract and documents — and the customers' systems stay the systems of record on their side (SAP PM at four sites, Maximo at Gawler Deep); the workspace cross-references the work-order number in both rather than becoming another place to look.",
    cov: 'full',
    agent: 'Work order',
    links: [
      { label: 'Connectors', action: act('connectors') },
      { label: 'Service register (100 assets)', action: act('data'), openDoc: 'x:L-18_MXASSET.json' },
      { label: 'Gawler Deep Maximo export', action: act('data'), openDoc: 'x:MXWO_GDEEP' },
      { label: 'SAP PM export', action: act('data'), openDoc: 'sap:sap_pm_orders_export.json' },
    ],
    stat: [
      x('CONN.length'),
      t(' connectors · '),
      x('Object.keys(B.mx).length'),
      t(' asset records in the service register · SAP PM '),
      x("WOS.filter(w=>w.mine!=='GD').length.toLocaleString()"),
      t(' WOs · Maximo '),
      x("WOS.filter(w=>w.mine==='GD').length.toLocaleString()"),
      t(' WOs'),
    ],
  },
];
